import React, { FC, useEffect, useState } from 'react';
import { ReactComponent as EditIcon } from 'assets/icons/edit-blue.svg';
import {
  FileUploader,
  Input,
  InputWithSymbol,
  Multiselect,
  BaseModal,
  ToastMessage,
  formatPhoneNumber
} from '@cokitchen/cokitchen-components';
import EditRiderDetailsFooter from './edit-rider-details-footer';
import { toast } from 'react-toastify';
import { EditRiderType } from 'models/rider.model';
import { apiService } from 'utils/api';
import { GENDER_OPTIONS, STORAGE_KEYS } from 'utils/constants';
import { RiderFieldType } from 'components/logistics/add-riders/types';
import { CoKitchenType } from 'models/cokitchen.model';
import SelectBrand from 'components/base/select-brand';

type locationType = {
  text: string;
  value: string;
};

type EditRiderDetailsProps = {
  show: boolean;
  onClose: () => void;
  rider: Partial<EditRiderType>;
};

const EditRiderDetailsModal: FC<EditRiderDetailsProps> = ({ show, onClose, rider }) => {
  const emptyForm = {
    first_name: rider.first_name,
    last_name: rider.last_name,
    email: rider.email,
    phone_number: rider.phone_number,
    user_gender: rider.user_gender ?? 'Male',
    profile_image: rider.profile_image,
    user_id: rider.id,
    cokitchen_id: rider.cokitchen_id,
    cokitchen_polygon_ids: rider.cokitchen_polygon_ids,
    brand_ids: rider?.brand_ids
  };

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const [locations, setLocations] = useState<locationType[]>([]);
  const cokitchenArray: CoKitchenType[] = JSON.parse(
    localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST) ?? '[]'
  );

  const options: locationType[] = [];

  const getLocationOptions = (item: CoKitchenType[]) => {
    for (let i = 0; i < item?.length; i++) {
      if (item)
        options.push({
          value: item[i]?.id,
          text: item[i]?.name
        });
    }
  };

  useEffect(() => {
    getLocationOptions(cokitchenArray);
  }, [cokitchenArray]);

  useEffect(() => {
    if (options) setLocations(options);
  }, []);

  const isDisabled = () => {
    return !formData.email;
  };

  useEffect(() => {
    setFormData(emptyForm);
  }, [rider]);

  const sendDetails = async () => {
    try {
      setLoading(true);
      const res = await apiService.put(`logistics/admin/update-user/${rider.id}`, {
        baseUrl: process.env.REACT_APP_ORDER_STATS_API_KEY,
        data: Object.fromEntries(
          Object.entries({
            ...formData,
            phone_number: formatPhoneNumber(formData.phone_number ?? '')
          }).filter(([key, value]) => {
            if (key === 'user_id') {
              return false;
            }

            if (key === 'brand_ids') {
              return JSON.stringify(value) !== JSON.stringify(rider.brand_ids);
            }
            if (key === 'phone_number') {
              return formatPhoneNumber(rider.phone_number ?? '') !== value;
            }
            return value !== (rider as any)[key];
          })
        )
      });
      toast(<ToastMessage type='success' message='Edited rider successfully' />);
      setFormData({ ...formData, ...res.data?.data });
      onClose();
    } catch (error: any) {
      toast(<ToastMessage type='error' message={error.response.data.message} />);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRiders = async (e: any, field: RiderFieldType) => {
    const rider = { ...formData };
    if (field === 'cokitchen_id') {
      rider.cokitchen_id = e;
      rider.cokitchen_polygon_ids = [];
    } else if (field === 'cokitchen_polygon_ids') {
      rider.cokitchen_polygon_ids = e;
    } else if (field === 'user_gender') {
      rider.user_gender = e;
    } else {
      rider[field] = e.target.value;
    }
    setFormData(rider);
  };

  const getPolygonOptions = (id: string) => {
    const cokitchen = cokitchenArray.find((cokitchen) => cokitchen.id === id);
    const cokitchenPolygons = cokitchen?.cokitchen_polygons;
    return (
      cokitchenPolygons?.map((polygon) => ({
        value: polygon.id,
        text: polygon.name
      })) || []
    );
  };

  return (
    <BaseModal
      position='right'
      isOpen={show}
      onRequestClose={onClose}
      title='Edit Rider Details'
      titleIcon={<EditIcon />}
      footer={
        <EditRiderDetailsFooter loading={loading} onConfirm={sendDetails} disabled={isDisabled()} />
      }>
      <form onSubmit={(ev) => ev.preventDefault()} className='space-y-4'>
        <div className='grid md:grid-cols-2 gap-6'>
          <Input
            name='first_name'
            label='First name'
            placeholder='First name'
            small
            disabled
            value={formData?.first_name}
          />
          <Input
            name='last_name'
            label='Last name'
            placeholder='Last name'
            small
            disabled
            value={formData?.last_name}
          />
        </div>
        <Input
          name='email'
          label='Email'
          placeholder='Company email'
          small
          disabled
          value={formData?.email}
        />
        <InputWithSymbol
          type='phone'
          name='phone_number'
          label='Phone Number'
          placeholder='07000000000'
          small
          onChange={(e) => handleEditRiders(e, 'phone_number')}
          value={formData?.phone_number || ''}
          symbol='flag'
        />
        <div>
          <label className='font-medium'>Gender</label>
          <Multiselect
            placeholder='Select Gender'
            values={[formData.user_gender]}
            options={GENDER_OPTIONS}
            onChange={(e) => {
              handleEditRiders(e[0], 'user_gender');
            }}
            isSingle
          />
        </div>
        <FileUploader
          disabled
          token={localStorage.getItem(STORAGE_KEYS.TOKEN) as string}
          fileUrls={formData.profile_image ? [formData.profile_image] : []}
          setFileUrls={(files) => (formData.profile_image = files[0])}
        />
        <div>
          <label className='font-medium'>Which cokitchen will this rider operate at</label>
          <Multiselect
            placeholder='Select cokitchen'
            isSingle
            values={formData.cokitchen_id ? [formData.cokitchen_id] : []}
            options={locations}
            onChange={(e) => {
              handleEditRiders(e[0], 'cokitchen_id');
            }}
          />
        </div>
        {formData.cokitchen_id && (
          <div>
            <label className='font-medium'>Which polygons will he be plotting</label>
            <Multiselect
              placeholder='Select polygon'
              values={formData.cokitchen_polygon_ids ? formData.cokitchen_polygon_ids : []}
              options={getPolygonOptions(formData.cokitchen_id)}
              onChange={(e) => {
                handleEditRiders(e, 'cokitchen_polygon_ids');
              }}
            />
          </div>
        )}
        <SelectBrand
          label={'Select specific brands to add rider to'}
          defaultValue={formData.brand_ids}
          setBrands={(e) => {
            setFormData({ ...formData, brand_ids: e });
          }}
        />
      </form>
    </BaseModal>
  );
};

export default EditRiderDetailsModal;
